/**
 * Merko Integrations — external write operations.
 *
 * Three operations exist, each gated on per-app `writes_enabled: true` after
 * separate live certification, and disabled by default. There is deliberately
 * no send, publish, delete or subscribe operation, and nothing here may invent
 * one.
 *
 * The lifecycle is preview → *separate deliberate* confirm → poll. Two parts of
 * it are safety properties rather than conveniences:
 *
 *   • A preview's `fields` are rendered **exactly** as returned, as text. HTML
 *     and content must never be injected live into a Merko page.
 *   • `outcome_unknown` means the provider write may already have happened. The
 *     user is told to inspect the provider, and no automatic retry is offered.
 */

import type { ActionOperation, ActionStatus, AppId, ExternalAction } from "./types";

/** Longest value for an `html`/`content` field. */
export const LONG_FIELD_MAX = 16_000;
/** Longest value for every other field. */
export const SHORT_FIELD_MAX = 254;

export interface ActionField {
    key: string;
    label: string;
    required: boolean;
    /** `html`/`content` get the long cap. */
    long?: boolean;
    hint?: string;
}

export interface ActionSchema {
    appId: AppId;
    operation: ActionOperation;
    label: string;
    fields: ActionField[];
    /** Shown to the user before they confirm, alongside the server's warning. */
    warning: string;
    /** True when at least one optional field must also be supplied. */
    requiresOneEdit?: boolean;
}

export const ACTION_SCHEMAS: ActionSchema[] = [
    {
        appId: "wordpress",
        operation: "create_draft",
        label: "Create a draft post",
        fields: [
            {
                key: "site_id",
                label: "Site ID",
                required: true,
                hint: "A positive whole number, sent as a string.",
            },
            { key: "title", label: "Title", required: true },
            { key: "content", label: "Content", required: true, long: true },
        ],
        warning: "Creates a draft only. It does not publish.",
    },
    {
        appId: "klaviyo",
        operation: "create_template",
        label: "Create a template",
        fields: [
            { key: "name", label: "Template name", required: true },
            { key: "html", label: "HTML", required: true, long: true },
        ],
        warning: "Creates a template only. It does not send.",
    },
    {
        appId: "intercom",
        operation: "update_contact",
        label: "Update a contact",
        fields: [
            { key: "contact_id", label: "Contact ID", required: true },
            { key: "name", label: "Name", required: false },
            { key: "email", label: "Email", required: false },
            { key: "phone", label: "Phone", required: false },
        ],
        warning: "Changes may trigger automations in Intercom.",
        requiresOneEdit: true,
    },
];

export const schemaFor = (
    appId: AppId,
    operation: ActionOperation,
): ActionSchema | undefined =>
    ACTION_SCHEMAS.find((s) => s.appId === appId && s.operation === operation);

/** Operations available for an app. Empty for every app but the three above. */
export const operationsFor = (appId: AppId): ActionSchema[] =>
    ACTION_SCHEMAS.filter((s) => s.appId === appId);

const DECIMAL_POSITIVE = /^[1-9][0-9]*$/;

export interface ActionValidation {
    valid: boolean;
    errors: Record<string, string>;
}

/**
 * Validate write fields before previewing.
 *
 * Unknown fields are rejected rather than dropped: the backend rejects them
 * too, and silently discarding what the user typed would be worse than saying
 * so.
 */
export function validateActionFields(
    appId: AppId,
    operation: ActionOperation,
    fields: Record<string, string>,
): ActionValidation {
    const schema = schemaFor(appId, operation);
    const errors: Record<string, string> = {};

    if (!schema) return { valid: false, errors: { _: "That operation is not supported." } };

    const allowed = new Set(schema.fields.map((f) => f.key));
    for (const key of Object.keys(fields)) {
        if (!allowed.has(key)) errors[key] = "This field is not accepted.";
    }

    for (const field of schema.fields) {
        const value = (fields[field.key] ?? "").trim();

        if (!value) {
            if (field.required) errors[field.key] = "This is required.";
            continue;
        }

        const max = field.long ? LONG_FIELD_MAX : SHORT_FIELD_MAX;
        if (value.length > max) {
            errors[field.key] = `Must be ${max.toLocaleString()} characters or fewer.`;
            continue;
        }

        // WordPress.com's site_id is documented as a positive decimal string.
        if (appId === "wordpress" && field.key === "site_id" && !DECIMAL_POSITIVE.test(value)) {
            errors[field.key] = "Must be a positive whole number.";
        }
    }

    // Intercom needs the contact plus at least one thing to change.
    if (schema.requiresOneEdit) {
        const optional = schema.fields.filter((f) => !f.required);
        const anyEdit = optional.some((f) => (fields[f.key] ?? "").trim().length > 0);
        if (!anyEdit) {
            errors._ = "Fill in at least one field to change.";
        }
    }

    return { valid: Object.keys(errors).length === 0, errors };
}

/** Action statuses after which polling is pointless. */
const TERMINAL: readonly ActionStatus[] = ["completed", "failed", "expired", "outcome_unknown"];

export const isTerminalAction = (status: ActionStatus): boolean =>
    (TERMINAL as readonly string[]).includes(status);

/**
 * What the UI should say and offer for an action's current status.
 *
 * `retry` is false for `outcome_unknown` on purpose: the write may have landed,
 * so replaying it risks doing the thing twice.
 */
export function describeActionStatus(action: ExternalAction): {
    label: string;
    detail: string;
    showResult: boolean;
    retry: boolean;
    verifyAtProvider: boolean;
} {
    switch (action.status) {
        case "pending":
            return { label: "Ready to confirm", detail: "Nothing has been written yet.", showResult: false, retry: false, verifyAtProvider: false };
        case "queued":
            return { label: "Queued", detail: "Queued — not written yet.", showResult: false, retry: false, verifyAtProvider: false };
        case "executing":
            return { label: "Writing…", detail: "The write is in progress.", showResult: false, retry: false, verifyAtProvider: false };
        case "completed":
            return { label: "Done", detail: "The write completed.", showResult: true, retry: false, verifyAtProvider: false };
        case "failed":
            return {
                label: "Didn’t run",
                // A pre-write validation failure: nothing reached the provider.
                detail: "This failed validation before anything was written.",
                showResult: false,
                retry: true,
                verifyAtProvider: false,
            };
        case "expired":
            return {
                label: "Expired",
                detail: "This preview expired. Take a fresh one.",
                showResult: false,
                retry: false,
                verifyAtProvider: false,
            };
        case "outcome_unknown":
            return {
                label: "Outcome unknown",
                detail:
                    "The write may have gone through. Check in the provider before trying again — " +
                    "this will not be retried automatically.",
                showResult: false,
                retry: false,
                verifyAtProvider: true,
            };
        default:
            // An unrecognised status is a refresh state, never an assumed success.
            return {
                label: "Unknown state",
                detail: "This action is in a state this app does not recognise. Refresh to check.",
                showResult: false,
                retry: false,
                verifyAtProvider: true,
            };
    }
}
